import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection, onSnapshot, query, where, doc, writeBatch, updateDoc,
  getDocs, orderBy, limit,
} from 'firebase/firestore';

function formatTime(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatScheduled(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function PublishingStatus() {
  const [todayItems, setTodayItems] = useState([]);
  const [generatingItems, setGeneratingItems] = useState([]);
  const [loadingToday, setLoadingToday] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  // Listener 1: all items scheduled for today (catches published, failed, pending)
  useEffect(() => {
    setLoadingToday(true);
    const q = query(
      collection(db, 'contentQueue'),
      where('scheduledFor', '>=', todayStart.toISOString()),
      where('scheduledFor', '<=', todayEnd.toISOString())
    );
    const unsub = onSnapshot(q, snap => {
      setTodayItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingToday(false);
      setLastRefresh(new Date());
    }, err => {
      console.error('PublishingStatus snapshot error:', err);
      setLoadingToday(false);
    });
    return unsub;
  }, []); // eslint-disable-line

  // Listener 2: anything currently generating globally (catches overdue articles from prior days)
  useEffect(() => {
    const q = query(collection(db, 'contentQueue'), where('status', '==', 'generating'));
    const unsub = onSnapshot(q, snap => {
      setGeneratingItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  // --- Pipeline Health data ---
  const [clients, setClients] = useState([]);
  const [allQueue, setAllQueue] = useState([]);
  const [freshness, setFreshness] = useState(null); // clientId -> latest upload Date (null until loaded)
  const [resumingId, setResumingId] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'clients'), snap =>
      setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => !c.isArchived))
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'contentQueue'), snap =>
      setAllQueue(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, []);

  // Newest Content Data upload per client (one-time fetch per client list change)
  useEffect(() => {
    if (clients.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(clients.map(async c => {
        try {
          const snap = await getDocs(query(
            collection(db, 'clients', c.id, 'files'),
            orderBy('uploadedAt', 'desc'),
            limit(1)
          ));
          const ts = snap.docs[0]?.data()?.uploadedAt;
          return [c.id, ts?.toDate ? ts.toDate() : null];
        } catch {
          return [c.id, null];
        }
      }));
      if (!cancelled) setFreshness(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [clients]);

  const resumePublishing = async (clientId) => {
    setResumingId(clientId);
    try {
      await updateDoc(doc(db, 'clients', clientId), { noContentQueue: false });
    } finally {
      setResumingId(null);
    }
  };

  const daysAgo = (date) => Math.floor((Date.now() - date.getTime()) / 86400000);

  // Per-client health rows — blog clients only (has the service or has queue history)
  const healthRows = clients
    .filter(c => (c.services || []).includes('Blog Optimization') || allQueue.some(q => q.clientId === c.id))
    .map(c => {
      const items = allQueue.filter(q => q.clientId === c.id);
      const publishedItems = items.filter(q => q.postUrl && q.generatedAt);
      const lastPublish = publishedItems.length > 0
        ? publishedItems.reduce((max, q) => {
            const d = q.generatedAt?.toDate ? q.generatedAt.toDate() : new Date(q.generatedAt);
            return d > max ? d : max;
          }, new Date(0))
        : null;
      const queueCount = items.filter(q => ['planned', 'generating'].includes(q.status)).length;
      const failedItem = items.find(q => q.status === 'planned' && q.genError && !q.dismissed);
      const hasCreds = c.publishPlatform === 'webflow'
        ? !!(c.webflowApiToken && c.webflowCollectionId)
        : !!(c.wpApiKey || (c.wpUsername && c.wpAppPassword));
      const newestUpload = freshness ? freshness[c.id] : undefined;

      const issues = [];
      if (c.noContentQueue) issues.push({ level: 'error', msg: 'Publishing paused (opted out)', action: 'resume' });
      if (!hasCreds) issues.push({ level: 'error', msg: 'No publishing credentials' });
      if (failedItem) issues.push({ level: 'error', msg: `Failed: ${(failedItem.genError || '').slice(0, 80)}` });
      if (queueCount === 0) issues.push({ level: 'warn', msg: 'Queue empty' });
      else if (queueCount <= 2) issues.push({ level: 'warn', msg: `Queue low (${queueCount})` });
      if (lastPublish && daysAgo(lastPublish) > 14 && !c.noContentQueue) issues.push({ level: 'warn', msg: `No publish in ${daysAgo(lastPublish)} days` });
      if (freshness && (!newestUpload || daysAgo(newestUpload) > 60)) issues.push({ level: 'warn', msg: newestUpload ? `Client data ${daysAgo(newestUpload)}d old` : 'No client data uploaded' });

      const level = issues.some(i => i.level === 'error') ? 'error' : issues.length > 0 ? 'warn' : 'ok';
      return { client: c, lastPublish, queueCount, newestUpload, issues, level };
    })
    .sort((a, b) => {
      const rank = { error: 0, warn: 1, ok: 2 };
      if (rank[a.level] !== rank[b.level]) return rank[a.level] - rank[b.level];
      return (a.client.businessName || '').localeCompare(b.client.businessName || '');
    });

  const healthCounts = {
    error: healthRows.filter(r => r.level === 'error').length,
    warn: healthRows.filter(r => r.level === 'warn').length,
    ok: healthRows.filter(r => r.level === 'ok').length,
  };

  // Merge: generating items that aren't already in todayItems
  const todayIds = new Set(todayItems.map(i => i.id));
  const extraGenerating = generatingItems.filter(i => !todayIds.has(i.id));
  const allItems = [...todayItems, ...extraGenerating];

  const generating = allItems.filter(i => i.status === 'generating');
  const published  = allItems.filter(i => ['publish', 'future', 'draft'].includes(i.status) && i.postUrl);
  const errors     = allItems.filter(i => i.status === 'planned' && i.genError && !i.dismissed);
  const pending    = allItems.filter(i => i.status === 'planned' && !i.genError && !i.dismissed);

  const [retrying, setRetrying] = useState(null);
  const [dismissing, setDismissing] = useState(null);

  const handleRetry = async (item) => {
    setRetrying(item.id);
    await updateDoc(doc(db, 'contentQueue', item.id), { genError: null });
    setRetrying(null);
  };

  const handleDismiss = async (item) => {
    setDismissing(item.id);
    await updateDoc(doc(db, 'contentQueue', item.id), { genError: null, dismissed: true });
    setDismissing(null);
  };

  const handleClearPublished = async () => {
    if (!window.confirm(`Delete all ${published.length} published article record${published.length !== 1 ? 's' : ''} from the queue? The articles remain live on the WordPress sites — this only removes them from the Hub queue.`)) return;
    setClearing(true);
    const batch = writeBatch(db);
    published.forEach(item => batch.delete(doc(db, 'contentQueue', item.id)));
    await batch.commit();
    setClearing(false);
  };

  const statusLabel = (item) => {
    if (item.status === 'publish')  return { label: '● Published',  color: '#4ade80',  bg: '#0a1f12', border: '#16a34a44' };
    if (item.status === 'future')   return { label: '◷ Scheduled',  color: '#eab308',  bg: '#1a1500', border: '#a16207aa' };
    if (item.status === 'draft')    return { label: '◎ Draft',      color: '#818cf8',  bg: 'rgba(99,102,241,.1)', border: '#4f46e533' };
    return null;
  };

  const Section = ({ title, count, color, children, action }) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
            {title}
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 10, background: color + '22', color, border: `1px solid ${color}44` }}>
            {count}
          </span>
        </div>
        {action}
      </div>
      {children}
    </div>
  );

  const EmptyRow = ({ msg }) => (
    <div style={{ padding: '18px 20px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border)' }}>
      {msg}
    </div>
  );

  return (
    <div className="page" style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Publishing Status</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Today's article activity across all clients — {formatScheduled(new Date().toISOString())}
          </p>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Live · updated {formatTime(lastRefresh)}
        </div>
      </div>

      {loadingToday ? (
        <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner"></div></div>
      ) : (
        <>
          {/* GENERATING */}
          <Section title="Currently Generating" count={generating.length} color="#fb923c">
            {generating.length === 0 ? (
              <EmptyRow msg="Nothing generating right now" />
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {generating.map((item, i) => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 20px', borderBottom: i < generating.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <span className="spinner" style={{ width: 14, height: 14, flexShrink: 0 }}></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: 13, overflowWrap: 'anywhere' }}>
                        {item.title || '(Untitled)'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.clientName}</div>
                    </div>
                    <div style={{ fontSize: 11, color: '#fb923c', background: '#1a1000', border: '1px solid #c2410c44', padding: '3px 10px', borderRadius: 20, flexShrink: 0 }}>
                      ⟳ Generating
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* PUBLISHED TODAY */}
          <Section
            title="Published Today"
            count={published.length}
            color="#4ade80"
            action={
              published.length > 0 && (
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={handleClearPublished}
                  disabled={clearing}
                  style={{ fontSize: 11 }}
                >
                  {clearing ? 'Clearing…' : `🗑 Clear list (${published.length})`}
                </button>
              )
            }
          >
            {published.length === 0 ? (
              <EmptyRow msg="No articles published yet today" />
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {published.map((item, i) => {
                  const st = statusLabel(item);
                  return (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 20px', borderBottom: i < published.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: 13, overflowWrap: 'anywhere' }}>
                          {item.title || '(Untitled)'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          {item.clientName}
                          {item.generatedAt && (
                            <span style={{ marginLeft: 8 }}>· {formatTime(item.generatedAt)}</span>
                          )}
                        </div>
                      </div>
                      {st && (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, border: '1px solid', background: st.bg, borderColor: st.border, color: st.color, flexShrink: 0 }}>
                          {st.label}
                        </span>
                      )}
                      {item.postUrl && (
                        <a href={item.postUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none', flexShrink: 0 }}>
                          ↗ View
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* ERRORS */}
          <Section title="Publishing Errors" count={errors.length} color="#f87171">
            {errors.length === 0 ? (
              <EmptyRow msg="No errors today" />
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {errors.map((item, i) => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '13px 20px', borderBottom: i < errors.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#f87171', flexShrink: 0, marginTop: 4 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: 13, overflowWrap: 'anywhere' }}>
                        {item.title || '(Untitled)'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.clientName}</div>
                      <div style={{ fontSize: 12, color: '#f87171', marginTop: 5, lineHeight: 1.5 }}>
                        {item.genError}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button
                        onClick={() => handleRetry(item)}
                        disabled={retrying === item.id || dismissing === item.id}
                        style={{ fontSize: 11, color: retrying === item.id ? '#6b7280' : '#818cf8', background: 'rgba(99,102,241,.1)', border: '1px solid #4f46e533', padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap', cursor: 'pointer' }}
                      >
                        {retrying === item.id ? '…' : '↻ Retry now'}
                      </button>
                      <button
                        onClick={() => handleDismiss(item)}
                        disabled={retrying === item.id || dismissing === item.id}
                        style={{ fontSize: 11, color: dismissing === item.id ? '#6b7280' : '#9ca3af', background: 'rgba(156,163,175,.08)', border: '1px solid #374151', padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap', cursor: 'pointer' }}
                      >
                        {dismissing === item.id ? '…' : '✕ Dismiss'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* PENDING (not yet processed today) */}
          {pending.length > 0 && (
            <Section title="Pending Today" count={pending.length} color="#818cf8">
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {pending.map((item, i) => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 20px', borderBottom: i < pending.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: 13, overflowWrap: 'anywhere' }}>
                        {item.title || '(Untitled)'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{item.clientName}</div>
                    </div>
                    <span style={{ fontSize: 11, color: '#818cf8', background: 'rgba(99,102,241,.1)', border: '1px solid #4f46e533', padding: '3px 10px', borderRadius: 20, flexShrink: 0 }}>
                      ◎ Queued
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* PIPELINE HEALTH — all blog clients at a glance */}
          <Section
            title="Pipeline Health"
            count={healthRows.length}
            color="#818cf8"
            action={
              <div style={{ display: 'flex', gap: 6, fontSize: 11 }}>
                {healthCounts.error > 0 && <span style={{ color: '#f87171', background: '#2a0a0a', border: '1px solid #7f1d1d55', padding: '2px 9px', borderRadius: 10, fontWeight: 700 }}>{healthCounts.error} need attention</span>}
                {healthCounts.warn > 0 && <span style={{ color: '#eab308', background: '#1a1500', border: '1px solid #a1620755', padding: '2px 9px', borderRadius: 10, fontWeight: 700 }}>{healthCounts.warn} warnings</span>}
                <span style={{ color: '#4ade80', background: '#0a1f12', border: '1px solid #16a34a44', padding: '2px 9px', borderRadius: 10, fontWeight: 700 }}>{healthCounts.ok} healthy</span>
              </div>
            }
          >
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '16px 1fr 110px 70px 100px', gap: 12, padding: '10px 20px', borderBottom: '1px solid var(--border)', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', alignItems: 'center' }}>
                <span></span>
                <span>Client / Issues</span>
                <span>Last Publish</span>
                <span>Queue</span>
                <span>Data Updated</span>
              </div>
              {healthRows.length === 0 ? (
                <div style={{ padding: '18px 20px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>No blog clients found.</div>
              ) : healthRows.map((row, i) => {
                const dotColor = row.level === 'error' ? '#f87171' : row.level === 'warn' ? '#eab308' : '#4ade80';
                return (
                  <div key={row.client.id} style={{ display: 'grid', gridTemplateColumns: '16px 1fr 110px 70px 100px', gap: 12, padding: '11px 20px', borderBottom: i < healthRows.length - 1 ? '1px solid var(--border)' : 'none', alignItems: 'start' }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor, marginTop: 4 }} />
                    <div style={{ minWidth: 0 }}>
                      <Link to={`/clients/${row.client.id}`} style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', textDecoration: 'none' }}>
                        {row.client.businessName}
                      </Link>
                      {row.issues.length > 0 && (
                        <div style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
                          {row.issues.map((issue, j) => (
                            <span key={j} style={{ fontSize: 11, color: issue.level === 'error' ? '#f87171' : '#eab308', background: issue.level === 'error' ? '#2a0a0a' : '#1a1500', border: `1px solid ${issue.level === 'error' ? '#7f1d1d55' : '#a1620755'}`, padding: '1px 8px', borderRadius: 10 }}>
                              {issue.msg}
                            </span>
                          ))}
                          {row.issues.some(issue => issue.action === 'resume') && (
                            <button
                              onClick={() => resumePublishing(row.client.id)}
                              disabled={resumingId === row.client.id}
                              style={{ fontSize: 11, fontWeight: 600, color: '#4ade80', background: '#0a1f12', border: '1px solid #16a34a55', padding: '1px 10px', borderRadius: 10, cursor: 'pointer' }}
                            >
                              {resumingId === row.client.id ? '…' : '▶ Resume'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <span style={{ fontSize: 12, color: row.lastPublish ? 'var(--text-secondary)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {row.lastPublish
                        ? (daysAgo(row.lastPublish) === 0 ? 'Today' : `${daysAgo(row.lastPublish)}d ago`)
                        : 'Never'}
                    </span>
                    <span style={{ fontSize: 12, color: row.queueCount === 0 ? '#f87171' : row.queueCount <= 2 ? '#eab308' : 'var(--text-secondary)' }}>
                      {row.queueCount} / 15
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {freshness === null ? '…' : row.newestUpload ? `${daysAgo(row.newestUpload)}d ago` : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
